/**
 * PushNavigatorService — pont SW → Router Angular pour la navigation
 * déclenchée depuis une notification push.
 *
 * Le service worker (ngsw-worker.js) capture l'événement
 * ``notificationclick`` et envoie un postMessage de la forme :
 *
 *   { type: 'vd-push-nav', url: '/inbox?conv=50', action?: 'open'|'take_over'|... , data?: {...} }
 *
 * Ce service écoute ces messages dans le contexte de la PWA et :
 *  1. Utilise ``Router.navigateByUrl`` pour naviguer proprement (préserve
 *     le state, déclenche les guards/resolvers, sans full reload).
 *  2. Si un ``action`` spécifique est demandé (ex. ``take_over``), il
 *     est passé via les query params (gérés par les pages cibles).
 *
 * Pourquoi pas ``client.navigate`` côté SW ? Parce que c'est gated dans
 * les PWA installées (standalone), bloqué sur Firefox/Safari, et fait un
 * reload complet du document sans préserver le state Angular. Le
 * postMessage + Router est la seule approche qui marche partout.
 */
import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';

interface PushNavMessage {
  type: 'vd-push-nav';
  url: string;
  absoluteUrl?: string;
  action?: string | null;
  data?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class PushNavigatorService {
  private router = inject(Router);
  private zone = inject(NgZone);
  private swUpdate = inject(SwUpdate);
  private inited = false;

  /** À appeler une seule fois au boot (depuis APP_INITIALIZER). */
  init(): void {
    if (this.inited) return;
    this.inited = true;

    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    // Listener postMessage SW → Router. Doit être attaché AVANT que le
    // SW n'envoie quoi que ce soit (idéalement au boot via APP_INITIALIZER).
    navigator.serviceWorker.addEventListener('message', (ev: MessageEvent) => {
      const msg = ev.data as PushNavMessage | null;
      if (!msg || msg.type !== 'vd-push-nav') return;
      console.log('[push-nav] received from SW:', msg);
      this.handleNav(msg);
    });

    // Force le check d'update du SW au boot ET toutes les heures :
    // sinon Angular ne ré-enregistre que toutes les 24h, et les
    // nouvelles features de notif (actions, routing) ne se propagent pas.
    this.scheduleSwUpdates();
  }

  private scheduleSwUpdates(): void {
    if (!this.swUpdate.isEnabled) return;
    // Check immédiat (au boot) pour propager le nouveau SW vite.
    setTimeout(() => {
      this.swUpdate.checkForUpdate().catch(() => {});
    }, 5_000);
    // Re-check toutes les 30 minutes (en plus du registerWhenStable d'Angular).
    setInterval(() => {
      this.swUpdate.checkForUpdate().catch(() => {});
    }, 30 * 60 * 1000);
  }

  private handleNav(msg: PushNavMessage): void {
    const url = msg.url || '/';
    // On force l'exécution dans la zone Angular pour que le change
    // detection se déclenche (postMessage arrive hors zone).
    this.zone.run(() => {
      const extras: NavigationExtras = {
        // Replace l'historique pour ne pas polluer la pile (l'utilisateur
        // n'a pas "navigué" volontairement).
        replaceUrl: false,
      };
      try {
        this.router.navigateByUrl(url, extras).catch((err) => {
          console.warn('[push-nav] navigateByUrl error', err);
          // Fallback ultime : full navigation.
          window.location.href = url;
        });
      } catch (err) {
        console.warn('[push-nav] handleNav error', err);
        window.location.href = url;
      }
    });
  }
}
